import { useState } from 'react'
import {
  Activity,
  AlertTriangle,
  Bell,
  FileText,
  FileWarning,
  FlaskConical,
  Inbox,
  PackageSearch,
  PanelLeft,
  Plug,
  RotateCcw,
  Ship,
  ShieldAlert,
  Truck,
  Users,
} from 'lucide-react'
import { SectionNav, type SettingsSection } from '../components/SectionNav'
import { CarriersSection } from '../sections/CarriersSection'
import { CofaNotificationsSection } from '../sections/CofaNotificationsSection'
import { DisciplinaryNotificationsSection } from '../sections/DisciplinaryNotificationsSection'
import { ExportLaneConfigSection } from '../sections/ExportLaneConfigSection'
import { FreightNotificationsSection } from '../sections/FreightNotificationsSection'
import { IntegrationsSection } from '../sections/IntegrationsSection'
import { OperationalAlertsSection } from '../sections/OperationalAlertsSection'
import { QuoteNotificationsSection } from '../sections/QuoteNotificationsSection'
import { RmaNotificationsSection } from '../sections/RmaNotificationsSection'
import { SalesPipelineNotificationsSection } from '../sections/SalesPipelineNotificationsSection'
import { SidebarSettingsSection } from '../sections/SidebarSection'
import { SystemHealthSection } from '../sections/SystemHealthSection'
import { UsersAndRolesSection } from '../sections/UsersAndRolesSection'
import { WarehouseIncidentNotificationsSection } from '../sections/WarehouseIncidentNotificationsSection'
import { InventoryDiscrepancyNotificationsSection } from '../sections/InventoryDiscrepancyNotificationsSection'
import { SampleOrderNotificationsSection } from '../sections/SampleOrderNotificationsSection'
import { SampleRequestNotificationsSection } from '../sections/SampleRequestNotificationsSection'
import { SampleRepsSection } from '../sections/SampleRepsSection'
import { UvivaSubmissionNotificationsSection } from '../sections/UvivaSubmissionNotificationsSection'
import { VendorPriceChangeNotificationsSection } from '../sections/VendorPriceChangeNotificationsSection'

type SectionId =
  | 'users'
  | 'operational-alerts'
  | 'notifications'
  | 'quote-notifications'
  | 'sales-pipeline-notifications'
  | 'rma-notifications'
  | 'sample-order-notifications'
  | 'sample-reps'
  | 'sample-request-notifications'
  | 'uviva-submission-notifications'
  | 'warehouse-incident-notifications'
  | 'inventory-discrepancy-notifications'
  | 'disciplinary-notifications'
  | 'carriers'
  | 'freight-pipeline-notifications'
  | 'vendor-price-change-notifications'
  | 'export-lane-config'
  | 'sidebar'
  | 'integrations'
  | 'health'

const SECTIONS: readonly SettingsSection<SectionId>[] = [
  { id: 'users', label: 'Users & Roles', icon: Users },
  { id: 'operational-alerts', label: 'Operational Alerts', icon: AlertTriangle },
  { id: 'notifications', label: 'CofA Notifications', icon: Bell },
  { id: 'quote-notifications', label: 'Quote Notifications', icon: FileText },
  { id: 'sales-pipeline-notifications', label: 'Sales Pipeline Notifications', icon: Inbox },
  { id: 'rma-notifications', label: 'Returns / RMA Notifications', icon: RotateCcw },
  { id: 'sample-order-notifications', label: 'SPI Sample Notifications', icon: FlaskConical },
  { id: 'sample-request-notifications', label: 'Sample Notifications', icon: FlaskConical, group: 'Uviva' },
  { id: 'sample-reps', label: 'Sample Reps', icon: Users, group: 'Uviva' },
  { id: 'uviva-submission-notifications', label: 'Shipment Notifications', icon: Bell, group: 'Uviva' },
  { id: 'warehouse-incident-notifications', label: 'Warehouse Incident Notifications', icon: ShieldAlert },
  { id: 'inventory-discrepancy-notifications', label: 'Inventory Discrepancy Notifications', icon: PackageSearch },
  { id: 'disciplinary-notifications', label: 'Disciplinary Notifications', icon: FileWarning },
  { id: 'carriers', label: 'Freight Carriers', icon: Truck },
  { id: 'freight-pipeline-notifications', label: 'Freight Pipeline Notifications', icon: Bell },
  { id: 'vendor-price-change-notifications', label: 'Vendor Price Change Notifications', icon: Bell },
  { id: 'export-lane-config', label: 'Export Lane Config', icon: Ship },
  { id: 'sidebar', label: 'Sidebar', icon: PanelLeft },
  { id: 'integrations', label: 'Integrations', icon: Plug },
  { id: 'health', label: 'System Health', icon: Activity },
]

export function SettingsPage() {
  const [active, setActive] = useState<SectionId>('users')

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-col gap-1 border-b border-border-subtle bg-surface px-6 py-3">
        <h1 className="text-role-page-title leading-tight tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-secondary">
          Admin-only configuration: users, notifications, integrations, system
          health.
        </p>
      </header>

      <div className="flex min-h-0 flex-1 overflow-x-auto">
        <SectionNav sections={SECTIONS} active={active} onSelect={setActive} />

        <main className="flex-1 overflow-auto bg-canvas">
          <div className="mx-auto max-w-[800px] px-6 py-6">
            {active === 'users' ? <UsersAndRolesSection /> : null}
            {active === 'operational-alerts' ? <OperationalAlertsSection /> : null}
            {active === 'notifications' ? <CofaNotificationsSection /> : null}
            {active === 'quote-notifications' ? <QuoteNotificationsSection /> : null}
            {active === 'sales-pipeline-notifications' ? <SalesPipelineNotificationsSection /> : null}
            {active === 'rma-notifications' ? <RmaNotificationsSection /> : null}
            {active === 'sample-reps' ? <SampleRepsSection /> : null}
            {active === 'sample-order-notifications' ? <SampleOrderNotificationsSection /> : null}
            {active === 'sample-request-notifications' ? <SampleRequestNotificationsSection /> : null}
            {active === 'uviva-submission-notifications' ? <UvivaSubmissionNotificationsSection /> : null}
            {active === 'warehouse-incident-notifications' ? <WarehouseIncidentNotificationsSection /> : null}
            {active === 'inventory-discrepancy-notifications' ? <InventoryDiscrepancyNotificationsSection /> : null}
            {active === 'disciplinary-notifications' ? <DisciplinaryNotificationsSection /> : null}
            {active === 'carriers' ? <CarriersSection /> : null}
            {active === 'freight-pipeline-notifications' ? <FreightNotificationsSection /> : null}
            {active === 'vendor-price-change-notifications' ? <VendorPriceChangeNotificationsSection /> : null}
            {active === 'export-lane-config' ? <ExportLaneConfigSection /> : null}
            {active === 'sidebar' ? <SidebarSettingsSection /> : null}
            {active === 'integrations' ? <IntegrationsSection /> : null}
            {active === 'health' ? <SystemHealthSection /> : null}
          </div>
        </main>
      </div>
    </div>
  )
}
